import { Size, t, type DocumentDeliveryView } from '@bms2/shared';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { StatusPill } from '../../components/controls/StatusPill';
import { useStatusResolver } from '../../shell/StatusResolver';
import { useDateFormat } from '../../shell/ViewerZone';
import { whenText } from '../../shell/viewerDates';
import styles from './DocumentPage.module.css';

/**
 * 🔒 The Delivered panel (piece 32; decisions 1225, 1226, T422): where a mailed
 * document's newest send stands, one line per recipient with the host's answer,
 * its time in the viewer's zone and the transport's TLS mark, as Zoho's
 * Delivered window draws it. A refusal or a bounce carries the host's own words
 * beneath its address. A document nothing mailed draws no panel. A send the
 * test environment's recording fake took draws one plain line saying nothing was
 * delivered, and its rows carry the address and time with no pill (decision 2481).
 */
export interface DeliveredPanelProps {
  delivery: DocumentDeliveryView | null | undefined;
  /** The viewer's zone, so the time reads in their own day (decision 0261). */
  timeZone?: string;
  /** The send window names the panel as the last send, since the one about to go is not in it yet. */
  heading?: string;
}

const SUMMARY: Record<DocumentDeliveryView['state'], Parameters<typeof t>[0]> = {
  Sending: 'document.delivery.sending',
  Sent: 'document.delivery.sent',
  DeliveryFailed: 'document.delivery.failed',
};

export function DeliveredPanel({ delivery, timeZone = 'UTC', heading }: DeliveredPanelProps) {
  const resolve = useStatusResolver();
  const dateFormat = useDateFormat();
  if (!delivery) return null;
  // 🔒 A send the test environment's fake took reads as recorded, never as delivered (T1196; decision 2481).
  const recorded = delivery.recorded === true;
  return (
    <section className={styles.block} data-delivered-panel={delivery.state} data-delivered-unconfirmed={delivery.unconfirmed ? 'true' : 'false'} data-delivered-recorded={recorded ? 'true' : 'false'}>
      <h2 className={styles.heading}>{heading ?? t('document.delivery.title')}</h2>
      <p className={styles.muted} data-delivered-summary="true">{recorded ? t('document.delivery.recorded') : t(SUMMARY[delivery.state])}</p>
      {delivery.unconfirmed && !recorded ? <p className={styles.muted} data-delivered-late="true">{t('document.delivery.unconfirmed')}</p> : null}
      {delivery.recipients.length === 0 ? <p className={styles.muted}>{t('document.delivery.noRows')}</p> : null}
      <DetailList look="separator" label={t('document.delivery.title')}>
        {delivery.recipients.map((r) => (
          <DetailRow
            key={r.address}
            data-delivered-recipient={r.address}
            data-delivered-status={r.status}
            title={r.address}
            detail={r.reason ? <span data-delivered-reason="true">{r.reason}</span> : null}
            columns={[{ value: whenText(r.at, timeZone, dateFormat), ariaLabel: t('document.delivery.time') }]}
            badges={recorded ? null : (
              <>
                <span aria-label={t('document.delivery.status')}>
                  <StatusPill entityType="RecipientDeliveryStatus" statusValue={r.status} resolve={resolve} size={Size.Small} />
                </span>
                <span aria-label={t('document.delivery.tls')} data-delivered-tls={r.transportSecurity}>
                  <StatusPill entityType="TransportSecurity" statusValue={r.transportSecurity} resolve={resolve} size={Size.Small} />
                </span>
              </>
            )}
          />
        ))}
      </DetailList>
    </section>
  );
}
